import {
  PlayerRating,
  MatchResult,
  TeamRating,
  TeamMatchResult,
  PlayerSummary,
  TournamentData,
} from "../types";
import { determineMatchResult } from "./matchResultHelper";
import { decompressJsonData } from "./jsonDecompressor";
import { DATA_VERSION } from "../dataVersion";

const dataUrl = (p: string) => `/calculation/${p}?v=${DATA_VERSION}`;

export const loadPlayerRatings = async (): Promise<PlayerRating[]> => {
  try {
    const response = await fetch(dataUrl("ratings-singles.json"));
    const data = await response.json();
    return Object.entries(data).map(([key, player]: [string, any]) => ({
      Name: player.name ?? key,
      PlayerId: Number(key),
      Rating: player.rating,
      "Games Played": player.gamesPlayed,
      "Games Won": player.gamesWon,
      "Games Lost": player.gamesLost,
      "Games Drawn": player.gamesDrawn,
      "Tournaments Played": player.tournamentsPlayed,
      "Rating Difference": player.ratingDifference,
      "Latest Tournament": player.latestTournament,
      "First Tournament": player.firstTournament,
      "Starting Rating": player.startingRating,
      "Rating History": player.ratingHistory,
    }));
  } catch (error) {
    console.error("Error loading player ratings:", error);
    return [];
  }
};

export const loadTeamRatings = async (): Promise<TeamRating[]> => {
  try {
    const response = await fetch(dataUrl("ratings-debel-teams.json"));
    const data = await response.json();
    return Object.entries(data).map(([teamName, team]: [string, any]) => ({
      Team: teamName,
      Rating: team.rating,
      "Games Played": team.gamesPlayed,
      "Games Won": team.gamesWon,
      "Games Lost": team.gamesLost,
      "Games Drawn": team.gamesDrawn,
      "Tournaments Played": team.tournamentsPlayed,
      "Rating Difference": team.ratingDifference,
      "Latest Tournament": team.latestTournament,
      "First Tournament": team.firstTournament,
      "Starting Rating": team.startingRating,
      "Rating History": team.ratingHistory,
    }));
  } catch (error) {
    console.error("Error loading team ratings:", error);
    return [];
  }
};

export const loadTeamMatches = async (): Promise<TeamMatchResult[]> => {
  try {
    const response = await fetch(
      dataUrl("detailed-match-results-teams.json")
    );
    const data = await response.json();
    const decompressed = decompressJsonData(data);
    return decompressed.results || [];
  } catch (error) {
    console.error("Error loading team matches:", error);
    return [];
  }
};

export const loadDetailedSinglesResults = async (): Promise<any[]> => {
  try {
    const response = await fetch(
      dataUrl("detailed-match-results-singles.json")
    );
    const data = await response.json();
    const decompressed = decompressJsonData(data);
    return decompressed.results || [];
  } catch (error) {
    console.error("Error loading detailed singles results:", error);
    return [];
  }
};

export const loadDetailedTeamResults = async (): Promise<any[]> => {
  try {
    const response = await fetch(
      dataUrl("detailed-match-results-teams.json")
    );
    const data = await response.json();
    const decompressed = decompressJsonData(data);
    return decompressed.results || [];
  } catch (error) {
    console.error("Error loading detailed team results:", error);
    return [];
  }
};

export const loadTeamPlayerRatings = async (): Promise<any[]> => {
  try {
    const response = await fetch(dataUrl("ratings-debel.json"));
    const data = await response.json();
    return Object.entries(data).map(([key, player]: [string, any]) => ({
      Player: player.name ?? key,
      PlayerId: Number(key),
      Rating: player.rating,
      "Games Played": player.gamesPlayed,
      "Games Won": player.gamesWon,
      "Games Lost": player.gamesLost,
      "Games Drawn": player.gamesDrawn,
      "Tournaments Played": player.tournamentsPlayed,
      "Rating Difference": player.ratingDifference,
      "Latest Tournament": player.latestTournament,
      "First Tournament": player.firstTournament,
      "Starting Rating": player.startingRating,
      "Rating History": player.ratingHistory,
      "Teams Played With": "",
      "Partner Stats": "",
    }));
  } catch (error) {
    console.error("Error loading team player ratings:", error);
    return [];
  }
};

export const loadDetailedTeamPlayerResults = async (): Promise<any[]> => {
  try {
    const response = await fetch(
      dataUrl("detailed-match-results-debel-single.json")
    );
    const data = await response.json();
    const decompressed = decompressJsonData(data);
    return decompressed.results || [];
  } catch (error) {
    console.error("Error loading detailed team player results:", error);
    return [];
  }
};

export const generatePlayerSummaries = (
  matches: MatchResult[]
): PlayerSummary[] => {
  const playerStats: { [key: string]: PlayerSummary } = {};

  matches.forEach((match) => {
    const player1 = match.Player1;
    const player2 = match.Player2;

    if (!player1 || !player2) return;

    if (!playerStats[player1]) {
      playerStats[player1] = {
        playerName: player1,
        totalMatches: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        winRate: 0,
        currentRating: 0,
        ratingChange: 0,
        tournamentsPlayed: [],
        opponents: {},
      };
    }

    if (!playerStats[player2]) {
      playerStats[player2] = {
        playerName: player2,
        totalMatches: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        winRate: 0,
        currentRating: 0,
        ratingChange: 0,
        tournamentsPlayed: [],
        opponents: {},
      };
    }

    playerStats[player1].totalMatches++;
    playerStats[player2].totalMatches++;

    if (
      !playerStats[player1].tournamentsPlayed.includes(
        match["Tournament Number"]
      )
    ) {
      playerStats[player1].tournamentsPlayed.push(match["Tournament Number"]);
    }
    if (
      !playerStats[player2].tournamentsPlayed.includes(
        match["Tournament Number"]
      )
    ) {
      playerStats[player2].tournamentsPlayed.push(match["Tournament Number"]);
    }

    if (!playerStats[player1].opponents[player2]) {
      playerStats[player1].opponents[player2] = {
        matches: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        winRate: 0,
      };
    }

    if (!playerStats[player2].opponents[player1]) {
      playerStats[player2].opponents[player1] = {
        matches: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        winRate: 0,
      };
    }

    playerStats[player1].opponents[player2].matches++;
    playerStats[player2].opponents[player1].matches++;

    const matchForHelper = {
      winner: match.Score === 1 ? 1 : match.Score === 0 ? 2 : ("draw" as const),
      matchResult: match.matchResult,
      score: match.Score,
    };

    const player1Result = determineMatchResult(matchForHelper, true);
    const player2Result = determineMatchResult(matchForHelper, false);

    if (player1Result === "W") {
      playerStats[player1].wins++;
      playerStats[player1].opponents[player2].wins++;
      playerStats[player2].losses++;
      playerStats[player2].opponents[player1].losses++;
    } else if (player1Result === "L") {
      playerStats[player1].losses++;
      playerStats[player1].opponents[player2].losses++;
      playerStats[player2].wins++;
      playerStats[player2].opponents[player1].wins++;
    } else {
      playerStats[player1].draws++;
      playerStats[player1].opponents[player2].draws++;
      playerStats[player2].draws++;
      playerStats[player2].opponents[player1].draws++;
    }

    playerStats[player1].currentRating = match["Rating After 1"];
    playerStats[player2].currentRating = match["Rating After 2"];
    playerStats[player1].ratingChange = match["Rating Change 1"];
    playerStats[player2].ratingChange = match["Rating Change 2"];
  });

  Object.values(playerStats).forEach((player) => {
    player.winRate =
      player.totalMatches > 0 ? (player.wins / player.totalMatches) * 100 : 0;
    Object.values(player.opponents).forEach((opponent) => {
      opponent.winRate =
        opponent.matches > 0 ? (opponent.wins / opponent.matches) * 100 : 0;
    });
  });

  return Object.values(playerStats);
};

export const generateTournamentData = (
  matches: MatchResult[]
): TournamentData[] => {
  const tournamentMap: { [key: number]: TournamentData } = {};

  matches.forEach((match) => {
    const tournamentNumber = match["Tournament Number"];

    if (!tournamentMap[tournamentNumber]) {
      tournamentMap[tournamentNumber] = {
        tournamentNumber,
        tournamentName: `Tournament #${tournamentNumber}`,
        matches: [],
        players: [],
        totalMatches: 0,
      };
    }

    // Try to extract tournament name from the match data if available
    if (
      match.tournamentName &&
      match.tournamentName !== `Tournament #${tournamentNumber}`
    ) {
      tournamentMap[tournamentNumber].tournamentName = match.tournamentName;
    }

    // Convert MatchResult to DetailedMatchResult format for compatibility
    const detailedMatch: any = {
      tournamentNumber,
      tournamentName: match.tournamentName,
      tournamentFile: `${tournamentNumber}.txt`,
      dataType: "UNKNOWN",
      matchIndex: 0,
      player1: match.Player1,
      player2: match.Player2,
      winner: match.Score === 1 ? 1 : match.Score === 0 ? 2 : "draw",
      score: match.Score,
      isSingleMatch: true,
      defaultRating: match["Default Rating"],
      player1RatingBefore: match["Rating Before 1"],
      player2RatingBefore: match["Rating Before 2"],
      player1RatingAfter: match["Rating After 1"],
      player2RatingAfter: match["Rating After 2"],
      player1RatingChange: match["Rating Change 1"],
      player2RatingChange: match["Rating Change 2"],
      expectedScore1: match["Expected Score 1"],
      expectedScore2: match["Expected Score 2"],
      kFactor1: match["K Factor 1"],
      kFactor2: match["K Factor 2"],
      timestamp: new Date().toISOString(),
    };

    tournamentMap[tournamentNumber].matches.push(detailedMatch);
    tournamentMap[tournamentNumber].totalMatches++;

    if (!tournamentMap[tournamentNumber].players.includes(match.Player1)) {
      tournamentMap[tournamentNumber].players.push(match.Player1);
    }
    if (!tournamentMap[tournamentNumber].players.includes(match.Player2)) {
      tournamentMap[tournamentNumber].players.push(match.Player2);
    }
  });

  return Object.values(tournamentMap).sort(
    (a, b) => b.tournamentNumber - a.tournamentNumber
  );
};

export const generateTournamentDataFromDetailedResults = (
  detailedResults: any[]
): TournamentData[] => {
  const tournamentMap: { [key: number]: TournamentData } = {};

  detailedResults.forEach((match) => {
    const tournamentNumber = match.tournamentNumber;

    if (!tournamentMap[tournamentNumber]) {
      tournamentMap[tournamentNumber] = {
        tournamentNumber,
        tournamentName:
          match.tournamentName || `Tournament #${tournamentNumber}`,
        matches: [],
        players: [],
        totalMatches: 0,
      };
    }

    // Update tournament name if we find a better one
    if (
      match.tournamentName &&
      match.tournamentName !== `Tournament #${tournamentNumber}`
    ) {
      tournamentMap[tournamentNumber].tournamentName = match.tournamentName;
    }

    tournamentMap[tournamentNumber].matches.push(match);
    tournamentMap[tournamentNumber].totalMatches++;

    // Add players (handle both singles and teams)
    if (
      match.player1 &&
      !tournamentMap[tournamentNumber].players.includes(match.player1)
    ) {
      tournamentMap[tournamentNumber].players.push(match.player1);
    }
    if (
      match.player2 &&
      !tournamentMap[tournamentNumber].players.includes(match.player2)
    ) {
      tournamentMap[tournamentNumber].players.push(match.player2);
    }
    if (match.team1) {
      match.team1.forEach((player: string) => {
        if (!tournamentMap[tournamentNumber].players.includes(player)) {
          tournamentMap[tournamentNumber].players.push(player);
        }
      });
    }
    if (match.team2) {
      match.team2.forEach((player: string) => {
        if (!tournamentMap[tournamentNumber].players.includes(player)) {
          tournamentMap[tournamentNumber].players.push(player);
        }
      });
    }
  });

  return Object.values(tournamentMap).sort(
    (a, b) => b.tournamentNumber - a.tournamentNumber
  );
};

export const getRatingBadgeClass = (rating: number): string => {
  if (rating < 1600) return "low";
  if (rating < 1800) return "medium";
  if (rating < 2000) return "high";
  return "very-high";
};
